import { useState, useEffect, useRef } from 'react';
import { api } from '../../api';
import { useToast } from '../../context/ToastContext';

/* CSS */
import styles from './InviteModal.module.css';
import shared from '../../styles/shared.module.css';
import shareStyles from './share.module.css';

/* Icons */
import arrow_back from '../../assets/icons/arrow_back.svg';
import default_avatar from '../../assets/icons/default_avatar.svg';
import access_link_clicked from '../../assets/icons/access_link_clicked.svg';
import access_link_unclicked from '../../assets/icons/access_link_unclicked.svg';
import select_chevron from '../../assets/icons/select_chevron.svg';
import tick from '../../assets/icons/tick_icon_filled_blue.png';

const ROLE_OPTIONS = [
    { value: 'editor', label: 'Editor' },
    { value: 'viewer', label: 'Viewer' },
];

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const InviteModal = ({ onBack, onDismiss, tripId, initialInvitee, invite }) => {

    const { showToast } = useToast();

    const [pendingInvitees, setPendingInvitees] = useState(
        initialInvitee ? [{ ...initialInvitee, role: 'viewer' }] : []
    );
    const [searchInput, setSearchInput] = useState('');
    const [searchRole, setSearchRole] = useState('viewer');
    const [searchResults, setSearchResults] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isInviting, setIsInviting] = useState(false);
    const [hoverCopyLink, setHoverCopyLink] = useState(false);
    const [openRoleMenuId, setOpenRoleMenuId] = useState(null);
    // Which suggestion is highlighted for keyboard nav (-1 = none)
    const [activeIndex, setActiveIndex] = useState(-1);

    const searchRef = useRef(null);
    const searchTimerRef = useRef(null);
    const roleDropdownRef = useRef(null);

    // Close dropdowns on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
            if (roleDropdownRef.current && !roleDropdownRef.current.contains(e.target)) {
                setOpenRoleMenuId(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchInput(value);

        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

        if (!value.trim()) {
            setSearchResults([]);
            setShowDropdown(false);
            setActiveIndex(-1);
            return;
        }

        searchTimerRef.current = setTimeout(async () => {
            try {
                const results = await api.get(`/share/users/search?q=${encodeURIComponent(value.trim())}`);
                // Filter out already-added invitees
                const filtered = results.filter(u => !pendingInvitees.some(p => p.email === u.email));
                setSearchResults(filtered);
                setShowDropdown(filtered.length > 0);
                setActiveIndex(-1);
            } catch (err) {
                // Surface the rate-limit case so the user knows why suggestions stopped
                if (err.status === 429) showToast(err.message, 'error');
            }
        }, 300);
    };

    const handleAddFromSearch = (user) => {
        if (pendingInvitees.some(p => p.email === user.email)) return;
        setPendingInvitees(prev => [...prev, {
            email: user.email,
            username: user.username,
            profile_picture_url: user.profile_picture_url,
            role: searchRole
        }]);
        setSearchInput('');
        setSearchResults([]);
        setShowDropdown(false);
        setActiveIndex(-1);
    };

    const handleAddEmail = () => {
        const email = searchInput.trim();
        if (!email) return;
        if (!isValidEmail(email)) {
            showToast('Invalid email address', 'error');
            return;
        }
        if (pendingInvitees.some(p => p.email === email)) return;
        setPendingInvitees(prev => [...prev, { email, role: searchRole }]);
        setSearchInput('');
        setSearchResults([]);
        setShowDropdown(false);
        setActiveIndex(-1);
    };

    // Keyboard navigation for the suggestion dropdown
    const handleSearchKeyDown = (e) => {
        const hasSuggestions = showDropdown && searchResults.length > 0;

        if (hasSuggestions && e.key === 'ArrowDown') {
            e.preventDefault(); // don't jump the text caret
            setActiveIndex(i => Math.min(i + 1, searchResults.length - 1));
            return;
        }
        if (hasSuggestions && e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
            return;
        }
        if (e.key === 'Enter') {
            // A highlighted suggestion wins over the raw text
            if (hasSuggestions && activeIndex >= 0) {
                handleAddFromSearch(searchResults[activeIndex]);
                return;
            }
            handleAddEmail();
        }
    };

    const handleRemove = (email) => {
        setPendingInvitees(prev => prev.filter(p => p.email !== email));
    };

    const handleChangeRole = (email, newRole) => {
        setPendingInvitees(prev => prev.map(p => p.email === email ? { ...p, role: newRole } : p));
    };

    const handleCopyLink = async () => {
        if (!invite?.invite_token) return;
        const joinUrl = `${window.location.origin}/join/${invite.invite_token}`;
        try {
            await navigator.clipboard.writeText(joinUrl);
            showToast('Link copied to clipboard', 'success');
        } catch {
            showToast('Failed to copy link', 'error');
        }
    };

    const handleInvite = async () => {
        if (pendingInvitees.length === 0 || isInviting) return;
        setIsInviting(true);
        try {
            const results = await Promise.allSettled(
                pendingInvitees.map(invitee =>
                    api.post(`/share/${tripId}/members`, { email: invitee.email, role: invitee.role })
                )
            );

            const succeeded = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.filter(r => r.status === 'rejected').length;

            if (succeeded > 0) {
                showToast(succeeded === 1 ? 'Invitation sent!' : `${succeeded} invitations sent!`, 'success');
            }
            if (failed > 0) {
                showToast(`${failed} invitation${failed > 1 ? 's' : ''} could not be sent.`, 'error');
            }

            onDismiss();
        } catch (err) {
            showToast(err.message || 'Failed to send invitations', 'error');
        } finally {
            setIsInviting(false);
        }
    };

    return (
        <div className={shared.modalOverlay}>
            <div className={styles.modalContent}>

                {/* Header */}
                <div className={styles.modalHeader}>
                    <button className={styles.backButton} onClick={onBack}>
                        <img src={arrow_back} alt="Back" className={styles.backIcon} />
                    </button>
                    <span className={styles.modalTitle}>Invite people</span>
                </div>

                {/* Search row */}
                <div className={styles.searchRow} ref={searchRef}>
                    <div className={styles.searchWrapper}>
                        <input
                            type="text"
                            className={styles.searchInput}
                            placeholder="Add people by email"
                            value={searchInput}
                            onChange={handleSearchChange}
                            onKeyDown={handleSearchKeyDown}
                        />
                        {showDropdown && searchResults.length > 0 && (
                            <div className={shareStyles.searchDropdown}>
                                {searchResults.map((user, index) => (
                                    <div
                                        key={user.id}
                                        className={`${shareStyles.searchResult} ${activeIndex === index ? shareStyles.searchResultActive : ''}`}
                                        onMouseEnter={() => setActiveIndex(index)}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleAddFromSearch(user);
                                        }}
                                    >
                                        <img
                                            src={user.profile_picture_url || default_avatar}
                                            alt={user.username}
                                            className={shareStyles.searchResultAvatar}
                                        />
                                        <div className={shareStyles.searchResultInfo}>
                                            <span className={shareStyles.searchResultName}>{user.username}</span>
                                            <span className={shareStyles.searchResultEmail}>{user.email}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div
                        className={styles.roleButton}
                        onClick={() => setOpenRoleMenuId(
                            openRoleMenuId === 'searchRole' ? null : 'searchRole'
                        )}
                    >
                        <span className={shareStyles.roleButtonTitle}>
                            {searchRole === 'editor' ? 'Editor' : 'Viewer'}
                        </span>
                        <img src={select_chevron} alt="Change role" className={shareStyles.roleButtonChevron} />
                        {openRoleMenuId === 'searchRole' && (
                            <div className={shareStyles.roleDropdown} ref={roleDropdownRef}>
                                {ROLE_OPTIONS.map(option => (
                                    <div
                                        key={option.value}
                                        className={styles.roleDropdown}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSearchRole(option.value);
                                            setOpenRoleMenuId(null);
                                        }}
                                    >
                                        <span>{option.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Pending invitees list */}
                {pendingInvitees.length > 0 && (
                    <ul className={styles.inviteeList}>
                        {pendingInvitees.map(invitee => (
                            <li key={invitee.email} className={styles.inviteeRow}>
                                <img
                                    src={invitee.profile_picture_url || default_avatar}
                                    alt={invitee.username || invitee.email}
                                    className={shareStyles.personAvatar}
                                />
                                <div className={shareStyles.personInfo}>
                                    {invitee.username && (
                                        <span className={styles.inviteeName}>{invitee.username}</span>
                                    )}
                                    <span className={styles.inviteeEmail}>{invitee.email}</span>
                                </div>

                                {/* Custom role dropdown — same pattern as ShareModal */}
                                <div
                                    className={shareStyles.roleButton}
                                    onClick={() => setOpenRoleMenuId(
                                        openRoleMenuId === invitee.email ? null : invitee.email
                                    )}
                                >
                                    <span className={shareStyles.roleButtonTitle}>
                                        {invitee.role === 'editor' ? 'Editor' : 'Viewer'}
                                    </span>
                                    <img src={select_chevron} alt="Change role" className={shareStyles.roleButtonChevron} />

                                    {openRoleMenuId === invitee.email && (
                                        <div className={shareStyles.roleDropdown} ref={roleDropdownRef}>
                                            {ROLE_OPTIONS.map(option => (
                                                <div
                                                    key={option.value}
                                                    className={shareStyles.roleDropdownItem}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleChangeRole(invitee.email, option.value);
                                                        setOpenRoleMenuId(null);
                                                    }}
                                                >
                                                    <div className={shareStyles.tickSlot}>
                                                        {option.value === invitee.role && (
                                                            <img src={tick} alt="Selected" className={shareStyles.tickIcon} />
                                                        )}
                                                    </div>
                                                    <span>{option.label}</span>
                                                    <div className={shareStyles.tickSlot} />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button
                                    className={styles.removeButton}
                                    onClick={() => handleRemove(invitee.email)}
                                >×</button>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Action buttons */}
                <div className={styles.actionButtons}>
                    <button
                        className={`${shareStyles.actionButton} ${styles.copyLinkButton}`}
                        onClick={handleCopyLink}
                        onMouseEnter={() => setHoverCopyLink(true)}
                        onMouseLeave={() => setHoverCopyLink(false)}
                    >
                        <img
                            src={hoverCopyLink ? access_link_clicked : access_link_unclicked}
                            alt="Copy link"
                            className={styles.copyLinkIcon}
                        />
                        <span>Copy link</span>
                    </button>
                    <div className={styles.rightActions}>
                        <button className={styles.cancelButton} onClick={onBack}>Cancel</button>
                        <button
                            className={`${shareStyles.actionButton} ${styles.inviteButton} ${isInviting ? shared.buttonLoading : ''}`}
                            onClick={handleInvite}
                            disabled={pendingInvitees.length === 0 || isInviting}
                        >
                            {isInviting ? 'Sending...' : 'Invite'}
                        </button>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default InviteModal;
